import { useTranslation } from 'react-i18next'
import Router from 'src/routes/Router'
import routes from 'src/routes/definitions'
import * as Yup from 'yup'
import { LangSwitchProps } from 'src/components/LangSwitch'

import { HeaderProps } from '../../components/Header'
import { Configuration } from '../../rest/types/Configuration'

const useHeader = ({
  configuration,
  currentLocale,
  langSwitch,
}: {
  configuration: Configuration
  currentLocale: string
  langSwitch?: LangSwitchProps
}): HeaderProps => {
  const { t } = useTranslation()

  const mainMenu = configuration.menus['header']?.entries || []
  const secondaryMenu = configuration.menus['menu-secondary']?.entries || []

  return {
    logoProps: {
      htmlTag: 'h1',
      title: t('logo.title'),
      linkProps: {
        href: Router.getRouteUrl(routes.home),
        locale: currentLocale,
      },
    },
    burgerProps: {
      texts: {
        open: t('header.burger.open'),
        close: t('header.burger.close'),
      },
    },
    externalLink: {
      label: t('header.externalLink.label'),
      href: t('header.externalLink.href'),
      target: '_blank',
      rel: 'noopener noreferrer',
    },
    langSwitchProps: langSwitch,
    menuProps: {
      open: false,
      links: mainMenu.map((item) => ({
        linkProps: {
          text: item.title || '',
          href: item.url || '',
          target: item.target || '',
        },
        ...(item.children &&
          item.children.length && {
            sub: item.children.map((childItem) => ({
              linkProps: {
                text: childItem.title || '',
                href: childItem.url || '',
                target: childItem.target || '',
              },
            })),
          }),
      })),
      bottomLinks: secondaryMenu.map((item) => ({
        linkProps: {
          text: item.title || '',
          href: item.url || '',
          target: item.target || '',
        },
        ...(item.children &&
          item.children.length && {
            sub: item.children.map((childItem) => ({
              linkProps: {
                text: childItem.title || '',
                href: childItem.url || '',
                target: childItem.target || '',
              },
            })),
          }),
      })),
    },
    searchProps: {
      button: {
        label: t('header.search.label'),
        'aria-label': t('header.search.label'),
      },
      form: {
        placeholder: t('header.search.form.placeholder'),
        submitLabel: t('header.search.form.submitLabel'),
        resetLabel: t('header.search.form.resetLabel'),
      },
      formikFormProps: {
        onSubmit: (v) => {
          Router.pushRoute(
            routes.search,
            { slug: v.term },
            { locale: currentLocale }
          )
        },
        initialValues: {
          term: '',
        },
        validationSchema: Yup.object().shape({
          term: Yup.string()
            .min(3, t('header.search.form.validation.min'))
            .required(t('yup.required')),
        }),
      },
    },
  }
}

export default useHeader
